<div>
    @if (metricsLoadError) {
        <div class="alert alert-warning py-2 small" role="alert">Dashboard metrics could not be loaded. Try refreshing the page.</div>
    }

    <div class="row g-3 mb-3">
        <div class="col-6 col-lg-3">
            <div class="card h-100 border-primary border-opacity-25">
                <div class="card-body py-3">
                    <div class="text-muted small text-uppercase">Inspections this week</div>
                    <div class="fs-3 fw-semibold">{{ metrics.kpi.inspectionsThisWeek }}</div>
                </div>
            </div>
        </div>
        <div class="col-6 col-lg-3">
            <div class="card h-100 border-primary border-opacity-25">
                <div class="card-body py-3">
                    <div class="text-muted small text-uppercase">Inspections this month</div>
                    <div class="fs-3 fw-semibold">{{ metrics.kpi.inspectionsThisMonth }}</div>
                </div>
            </div>
        </div>
        <div class="col-6 col-lg-3">
            <div class="card h-100 border-success border-opacity-25">
                <div class="card-body py-3">
                    <div class="text-muted small text-uppercase">Vehicles current</div>
                    <div class="fs-3 fw-semibold">
                        {{ metrics.kpi.vehiclesInspectedThisMonth }}
                        <span class="fs-6 text-muted">/ {{ metrics.kpi.totalInspectableVehicles }}</span>
                    </div>
                </div>
            </div>
        </div>
        <div class="col-6 col-lg-3">
            <div class="card h-100 border-danger border-opacity-50">
                <div class="card-body py-3">
                    <div class="text-muted small text-uppercase">Vehicles overdue</div>
                    <div class="fs-3 fw-semibold">{{ metrics.kpi.vehiclesOverdueThisMonth }}</div>
                </div>
            </div>
        </div>
    </div>

    <div class="row g-3 mb-3">
        <div class="col-lg-6">
            <div class="card h-100">
                <div class="card-body">
                    <h5 class="card-title">Inspections by week</h5>
                    <p class="small text-muted">Submitted inspections over the last 12 weeks.</p>
                    @if (metrics.weeklyCounts.length === 0) {
                        <p class="text-muted small mb-0">No submitted inspections in this period.</p>
                    } @else {
                        <div class="d-flex align-items-end gap-1 vi-bar-wrap">
                            @for (w of metrics.weeklyCounts; track w.label) {
                                <div class="flex-fill text-center">
                                    <div class="vi-bar rounded-top" [style.height.px]="(w.count / maxWeeklyCount()) * 120 || 4" [title]="w.label + ': ' + w.count"></div>
                                    <div class="small text-muted mt-1" style="font-size: 10px">{{ w.label }}</div>
                                    <div class="small fw-semibold">{{ w.count }}</div>
                                </div>
                            }
                        </div>
                    }
                </div>
            </div>
        </div>

        <div class="col-lg-6">
            <div class="card h-100">
                <div class="card-body">
                    <h5 class="card-title">Inspections by month</h5>
                    <p class="small text-muted">Submitted inspections over the last 6 months.</p>
                    @if (metrics.monthlyCounts.length === 0) {
                        <p class="text-muted small mb-0">No submitted inspections in this period.</p>
                    } @else {
                        <div class="d-flex align-items-end gap-1 vi-bar-wrap">
                            @for (m of metrics.monthlyCounts; track m.label) {
                                <div class="flex-fill text-center">
                                    <div class="vi-bar rounded-top" [style.height.px]="(m.count / maxMonthlyCount()) * 120 || 4" [title]="m.label + ': ' + m.count"></div>
                                    <div class="small text-muted mt-1">{{ m.label }}</div>
                                    <div class="small fw-semibold">{{ m.count }}</div>
                                </div>
                            }
                        </div>
                    }
                </div>
            </div>
        </div>
    </div>

    <div class="row g-3 mb-3">
        <div class="col-lg-6">
            <div class="card h-100">
                <div class="card-body">
                    <h5 class="card-title">Open non-compliant items by vehicle</h5>
                    <p class="small text-muted">Submitted inspections with checklist items still marked non-compliant and not corrected.</p>
                    @if (metrics.openNcByVehicle.length === 0) {
                        <p class="text-muted small mb-0">No open non-compliant items.</p>
                    } @else {
                        <div class="table-responsive">
                            <table class="table table-sm table-striped mb-0">
                                <thead>
                                    <tr>
                                        <th>Vehicle</th>
                                        <th class="text-end">Open items</th>
                                        @if (canViewInspectionList) {
                                            <th></th>
                                        }
                                    </tr>
                                </thead>
                                <tbody>
                                    @for (row of metrics.openNcByVehicle; track row.equipmentCode) {
                                        <tr>
                                            <td>
                                                {{ row.equipmentCode }}
                                                @if (row.vehicleDescription) {
                                                    <span class="text-muted small">{{ row.vehicleDescription }}</span>
                                                }
                                            </td>
                                            <td class="text-end">{{ row.openItems }}</td>
                                            @if (canViewInspectionList) {
                                                <td class="text-end">
                                                    <a
                                                        routerLink="/safety/vehicle-inspection"
                                                        [queryParams]="inspectionListQueryParams(row.focusInspectionId)"
                                                        class="small"
                                                    >
                                                        View
                                                    </a>
                                                </td>
                                            }
                                        </tr>
                                    }
                                </tbody>
                            </table>
                        </div>
                    }
                </div>
            </div>
        </div>

        <div class="col-lg-6">
            <div class="card h-100 border-danger border-opacity-50">
                <div class="card-body">
                    <div class="d-flex flex-wrap align-items-center gap-2 mb-2">
                        <h5 class="card-title mb-0">Vehicles overdue</h5>
                        <span class="badge rounded-pill text-bg-danger">{{ metrics.kpi.vehiclesOverdueThisMonth }}</span>
                    </div>
                    <p class="small text-muted">Active inspectable equipment past due: every 30 days, every 500 hours, or never inspected.</p>
                    @if (metrics.coverage.overdue.length === 0) {
                        <p class="text-muted small mb-0">All active inspectable vehicles are current.</p>
                    } @else {
                        <div class="vi-coverage-scroll">
                            <table class="table table-sm table-hover mb-0">
                                <thead class="vi-coverage-thead">
                                    <tr>
                                        <th>Vehicle</th>
                                        <th>Main Category</th>
                                        <th>Last inspection</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    @for (row of metrics.coverage.overdue; track row.equipmentCode) {
                                        <tr>
                                            <td>
                                                @if (canEditInspections) {
                                                    <button
                                                        type="button"
                                                        class="btn btn-link btn-sm p-0 align-baseline"
                                                        title="Create inspection for this vehicle"
                                                        (click)="addForEquipment.emit({ equipmentCode: row.equipmentCode, description: row.description })"
                                                    >
                                                        {{ formatEquipmentDisplay(row.equipmentCode, row.description) }}
                                                    </button>
                                                } @else {
                                                    {{ formatEquipmentDisplay(row.equipmentCode, row.description) }}
                                                }
                                            </td>
                                            <td class="small">{{ row.mainCategory }}</td>
                                            <td>
                                                @if (isHourlyRow(row)) {
                                                    @if (row.lastHourReading != null) {
                                                        <span>{{ row.lastHourReading | number: '1.0-0' }} hrs</span>
                                                        @if (row.hoursSinceLastInspection != null) {
                                                            <span class="text-muted small"> (+{{ row.hoursSinceLastInspection | number: '1.0-0' }} hrs)</span>
                                                        }
                                                    } @else {
                                                        <span class="text-danger small">No hour reading on file</span>
                                                    }
                                                } @else if (row.lastInspectionDate) {
                                                    {{ row.lastInspectionDate | date: 'mediumDate' }}
                                                    @if (row.daysSinceLastInspection != null) {
                                                        <span class="text-muted small"> ({{ row.daysSinceLastInspection }} days)</span>
                                                    }
                                                } @else {
                                                    <span class="text-danger small">Never inspected</span>
                                                }
                                            </td>
                                        </tr>
                                    }
                                </tbody>
                            </table>
                        </div>
                    }
                </div>
            </div>
        </div>

        <div class="col-lg-6">
            <div class="card h-100 border-success border-opacity-25">
                <div class="card-body">
                    <div class="d-flex flex-wrap align-items-center gap-2 mb-2">
                        <h5 class="card-title mb-0">Vehicles current</h5>
                        <span class="badge rounded-pill text-bg-success">{{ metrics.coverage.completed.length }}</span>
                    </div>
                    <p class="small text-muted">Active inspectable equipment with a recent inspection within the due interval.</p>
                    @if (metrics.coverage.completed.length === 0) {
                        <p class="text-muted small mb-0">No vehicles are currently compliant.</p>
                    } @else {
                        <div class="vi-coverage-scroll">
                            <table class="table table-sm table-hover mb-0">
                                <thead class="vi-coverage-thead">
                                    <tr>
                                        <th>Vehicle</th>
                                        <th>Main Category</th>
                                        <th>Last inspection</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    @for (row of metrics.coverage.completed; track row.equipmentCode) {
                                        <tr>
                                            <td>{{ row.equipmentCode }} <span class="text-muted small">{{ row.description }}</span></td>
                                            <td class="small">{{ row.mainCategory }}</td>
                                            <td>
                                                @if (isHourlyRow(row) && row.lastHourReading != null) {
                                                    {{ row.lastHourReading | number: '1.0-0' }} hrs
                                                } @else if (row.lastInspectionDate) {
                                                    {{ row.lastInspectionDate | date: 'mediumDate' }}
                                                }
                                            </td>
                                        </tr>
                                    }
                                </tbody>
                            </table>
                        </div>
                    }
                </div>
            </div>
        </div>
    </div>

    <div class="row g-3">
        <div class="col-12">
            <div class="card">
                <div class="card-body">
                    <h5 class="card-title">Conex and trailer inspection status</h5>
                    <p class="small text-muted">
                        Mark Conex and office trailer equipment inactive when it is not in use and does not require regular inspections.
                        Inactive equipment appears here only and is excluded from overdue and current lists.
                    </p>
                    @if (metrics.conexTrailerSettings.length === 0) {
                        <p class="text-muted small mb-0">No Conex or office trailer equipment found.</p>
                    } @else {
                        <div class="table-responsive">
                            <table class="table table-sm table-striped mb-0">
                                <thead>
                                    <tr>
                                        <th>Equipment</th>
                                        <th>Main Category</th>
                                        <th class="text-center">Inspection active</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    @for (row of metrics.conexTrailerSettings; track row.equipmentCode) {
                                        <tr>
                                            <td>
                                                {{ row.equipmentCode }}
                                                @if (row.description) {
                                                    <span class="text-muted small">{{ row.description }}</span>
                                                }
                                            </td>
                                            <td class="small">{{ row.mainCategory }}</td>
                                            <td class="text-center">
                                                @if (canEditInspections) {
                                                    <div class="form-check form-switch d-inline-block">
                                                        <input
                                                            class="form-check-input"
                                                            type="checkbox"
                                                            role="switch"
                                                            [id]="'vi-active-' + row.equipmentCode"
                                                            [checked]="row.isInspectionActive"
                                                            [disabled]="togglingEquipmentCode === row.equipmentCode"
                                                            (change)="toggleInspectionActive(row)"
                                                        />
                                                        <label class="form-check-label visually-hidden" [for]="'vi-active-' + row.equipmentCode">
                                                            Inspection active for {{ row.equipmentCode }}
                                                        </label>
                                                    </div>
                                                } @else {
                                                    @if (row.isInspectionActive) {
                                                        <span class="badge text-bg-success">Active</span>
                                                    } @else {
                                                        <span class="badge text-bg-secondary">Inactive</span>
                                                    }
                                                }
                                            </td>
                                        </tr>
                                    }
                                </tbody>
                            </table>
                        </div>
                    }
                </div>
            </div>
        </div>
    </div>
</div>
